// WhatsApp Business — panelde kullanılan etiketler ve saf yardımcılar (spec 2026-10-06).
// Sunucu karşılığı: Pupill-IOT-Server/src/modules/whatsapp

export const TEMPLATE_LABELS = {
  appointment_created: "Randevu oluşturuldu",
  appointment_rescheduled: "Randevu değişti",
  appointment_cancelled: "Randevu iptal edildi",
  appointment_reminder: "Randevu hatırlatma (24 saat önce)",
  // Manuel şablonlar — sunucu templates.js `title` ile aynı (elle senkron)
  contact_call: "Sizi arayalım",
  schedule_appointment: "Randevu planlayalım",
  checkup_due: "Kontrol zamanı",
  results_ready: "Sonuçlarınız hazır",
  document_ready: "Belgeniz hazır",
  check_app: "Uygulamayı kontrol edin",
  payment_reminder: "Ödeme hatırlatması",
};
export const TEMPLATE_ORDER = Object.keys(TEMPLATE_LABELS);

// Şablon durumu → { label, tone } (tone: StatusPill tonu)
const TEMPLATE_STATUS = {
  APPROVED: { label: "Onaylandı", tone: "success" },
  PENDING: { label: "İnceleniyor", tone: "warning" },
  REJECTED: { label: "Reddedildi", tone: "error" },
  PAUSED: { label: "Duraklatıldı", tone: "warning" },
  DISABLED: { label: "Devre dışı", tone: "error" },
  NOT_CREATED: { label: "Oluşturulamadı", tone: "neutral" },
};
export const templateStatusInfo = (status) =>
  TEMPLATE_STATUS[String(status || "").toUpperCase()] || { label: status || "—", tone: "neutral" };

/** Meta şablonu MARKETING'e aldıysa: pahalı + pazarlama izni ister — Pupill göndermez. */
export const isMarketing = (t) => String(t?.category || "").toUpperCase() === "MARKETING";
/** Gönderilebilir: onaylı ve pazarlamaya alınmamış. */
export const isSendable = (t) => String(t?.status || "").toUpperCase() === "APPROVED" && !isMarketing(t);
export const MARKETING_NOTE = "Meta pazarlama kategorisine aldı; Pupill bu şablonu göndermez.";

/** Sunucunun şablon dizisini sabit sıraya dizer; listede olmayan anahtarlar sona eklenir. */
export const sortTemplates = (templates) => {
  const list = Array.isArray(templates) ? templates : [];
  const rank = (k) => {
    const i = TEMPLATE_ORDER.indexOf(k);
    return i === -1 ? TEMPLATE_ORDER.length : i;
  };
  return [...list].sort((a, b) => rank(a?.key) - rank(b?.key));
};
export const templateLabel = (t) => TEMPLATE_LABELS[t?.key] || t?.name || t?.key || "—";

const PROBLEM_STATUSES = ["REJECTED", "PAUSED", "DISABLED", "NOT_CREATED"];

/**
 * Klinik ekranı için şablon özeti (şablonlar Pupill'in sabit seti; ayrıntılı liste
 * özetin altında, WhatsAppTemplateList).
 * @returns {{ label, tone, text }}
 */
export const templateSummary = (templates) => {
  const list = Array.isArray(templates) ? templates : [];
  const status = (t) => String(t?.status || "").toUpperCase();
  const total = list.length;
  const approved = list.filter(isSendable).length;
  if (list.some((t) => PROBLEM_STATUSES.includes(status(t)) || isMarketing(t))) {
    return {
      label: "Kontrol ediliyor",
      tone: "warning",
      text: "Bazı otomatik mesajlar Meta onayından geçmedi; Pupill destek ekibi ilgileniyor. Onaylı olanlar gönderilmeye devam eder.",
    };
  }
  if (total > 0 && approved === total) {
    return { label: "Hazır", tone: "success", text: "Tüm otomatik mesajlar gönderime hazır." };
  }
  return {
    label: "İnceleniyor",
    tone: "warning",
    text: `${approved}/${total} otomatik mesaj hazır; kalanlar Meta incelemesinde (genelde birkaç dakika, en fazla 24 saat).`,
  };
};

/**
 * Coexistence geçmiş aktarımı → bağlı ekranındaki kısa metin (yoksa null).
 * @param {{ historyDeclined?, historyProgress? } | null} coex
 */
export const historyImportText = (coex) => {
  if (!coex) return null;
  if (coex.historyDeclined) return "Geçmiş paylaşımı telefonda kapalı olduğu için eski sohbetler aktarılmadı.";
  const p = coex.historyProgress;
  if (p === null || p === undefined) return "Eski sohbetler bekleniyor; Meta aktarımı birkaç dakika içinde başlatır.";
  if (p >= 100) return "Son 6 aya kadarki sohbetler Mesajlar'a aktarıldı.";
  return `Eski sohbetler aktarılıyor (%${p}).`;
};

const QUALITY = { GREEN: "Yüksek", YELLOW: "Orta", RED: "Düşük" };
export const qualityLabel = (q) => QUALITY[String(q || "").toUpperCase()] || "—";
const QUALITY_TONE = { GREEN: "success", YELLOW: "warning", RED: "error" };
export const qualityTone = (q) => QUALITY_TONE[String(q || "").toUpperCase()] || "neutral";

const TIERS = {
  TIER_250: "Günde 250 kişi",
  TIER_1K: "Günde 1.000 kişi",
  TIER_2K: "Günde 2.000 kişi",
  TIER_10K: "Günde 10.000 kişi",
  TIER_100K: "Günde 100.000 kişi",
  TIER_UNLIMITED: "Sınırsız",
};
export const tierLabel = (t) => TIERS[String(t || "").toUpperCase()] || "—";

/**
 * Hesap görünümünden ekran durumu.
 * "connected" | "suspended" | "broken" (token geçersiz) | "not_connected"
 */
export const accountState = (account) => {
  const s = account?.status;
  if (s === "connected") return "connected";
  if (s === "suspended") return "suspended";
  if (s === "disconnected" && account?.disconnectedReason === "token_invalid") return "broken";
  return "not_connected";
};

/** Beyan güncel sürüm için kabul edilmiş mi? */
export const declarationAccepted = (declaration, account) => {
  if (declaration?.accepted) return true;
  const acc = account?.declaration;
  return Boolean(acc?.acceptedAt && declaration?.version && acc.version === declaration.version);
};

export const formatDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("tr-TR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

/** Hasta onayı nedeni → açıklama metni. */
export const optInReasonText = (view) => {
  switch (view?.reason) {
    case "app_off":
      return "Hasta uygulamadan kapattı";
    case "app_on":
      return "Hasta uygulamadan açtı";
    case "started":
      return "Hasta BAŞLA yazdı";
    case "stopped":
      return "Hasta DUR yazdı";
    default:
      return "Onay yok";
  }
};

/** WhatsApp mesaj teslim durumu → { label, ticks, tone } (yalnız klinik gönderimi). */
export const waStatusInfo = (status) => {
  switch (status) {
    case "sent":
      return { label: "Gönderildi", ticks: 1, tone: "muted" };
    case "delivered":
      return { label: "İletildi", ticks: 2, tone: "muted" };
    case "read":
      return { label: "Okundu", ticks: 2, tone: "read" };
    case "failed":
      return { label: "Gönderilemedi", ticks: 0, tone: "error" };
    default:
      return null;
  }
};

/** Kanal seçicinin varsayılanı: son hasta mesajının kanalı, yoksa uygulama. */
export const defaultChannel = (thread) => {
  const wa = thread?.whatsapp;
  if (!wa?.connected || !wa?.hasPhone) return "app";
  return thread?.conversation?.lastChannel === "whatsapp" ? "whatsapp" : "app";
};

export const WINDOW_CLOSED_NOTE = "Hasta son 24 saatte WhatsApp'tan yazmadığı için serbest mesaj gönderilemez.";

/** WhatsApp hattı klinik telefonundan farklı mı? (son 10 hane; ikisi de doluysa) */
export const phoneMismatch = (waNumber, clinicPhone) => {
  const key = (v) => String(v || "").replace(/\D/g, "").slice(-10);
  return Boolean(key(waNumber) && key(clinicPhone) && key(waNumber) !== key(clinicPhone));
};
